import { Stack, Typography } from '@mui/material';

import { DeleteButton } from '@modules/_shared/components/filters/advanced-filters/DeleteButton';
import { Enum } from '@modules/_shared/components/filters/advanced-filters/Enum';
import { InputObject } from '@modules/_shared/components/filters/advanced-filters/InputObject';
import { Scalar } from '@modules/_shared/components/filters/advanced-filters/Scalar';
import { deleteWhereClause } from '@modules/_shared/components/filters/Filters';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { useLocale } from '@shared/hooks/locale';

interface NodeProps {
  name: string;
  node: any;
  path: string[];
  where: any;
  setWhere: (where: any) => void;
}

export const Node = ({ name, node, path, where, setWhere }: NodeProps) => {
  const { t } = useLocale();
  const { getTypeFromPath } = useSchema();

  const sanitizedPath = !isNaN(parseInt(name)) ? path.slice(0, -1) : path;
  const type = getTypeFromPath(sanitizedPath);

  const handleDelete = () => setWhere(deleteWhereClause(where, path));

  if (!type) {
    console.info('No type found for', path);
    return (
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="error">
          {t('No type found for')} {path.join('.')}
        </Typography>
        <DeleteButton onClick={handleDelete} />
      </Stack>
    );
  }

  if (type?.kind === 'INPUT_OBJECT') {
    return (
      <InputObject
        name={name}
        node={node}
        path={path}
        where={where}
        setWhere={setWhere}
      />
    );
  }

  if (type?.kind === 'ENUM') {
    return (
      <Enum
        name={name}
        node={node}
        path={path}
        where={where}
        setWhere={setWhere}
      />
    );
  }

  if (type?.kind === 'SCALAR') {
    return (
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          component="label"
          sx={{
            fontStyle: 'italic',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
        <Scalar
          name={name}
          node={node}
          path={path}
          where={where}
          setWhere={setWhere}
        />
        <DeleteButton onClick={handleDelete} />
      </Stack>
    );
  }

  throw new Error('Unknown type kind: ' + type.kind);
};
